import type { ReactNode } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  ArrowRight,
  CircleCheckIcon,
  Package,
  TriangleAlertIcon,
} from "@/components/icons";
import {
  Badge,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  FieldContent,
  FieldError,
  FieldLabel,
  FieldLayout,
  FormActions,
  FormInput,
  FormRoot,
  RHF,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
  zodResolver,
} from "@/components/kit";
import { cn } from "@/lib/utils";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { useProductPickerQuery } from "@/modules/orders/presentation/api/queries";
import { ProductFormPageHeader } from "@/modules/products/presentation/ui/ProductFormPageHeader";
import { ProductFormSection } from "@/modules/products/presentation/ui/ProductFormSection";
import { formatLak } from "@/shared/lib/currency";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { z } from "zod";
import {
  useAssembleBundle,
  useBundleAssemblyPreview,
  useBundleDisassemblyPreview,
  useDisassembleBundle,
} from "../api/queries";
import type {
  BundleAssemblyPreviewDTO,
  BundleDisassemblyPreviewDTO,
} from "../../domain/types";

const FormSchema = z.object({
  bundleVariantId: z.string().min(1, "ເລືອກເຊັດ"),
  quantity: z.coerce.number().int().min(1),
  note: z.string().max(500).optional(),
});

type FormValues = z.infer<typeof FormSchema>;

const ASSEMBLE_FLOW_STEPS = [
  { title: "ເລືອກເຊັດ", detail: "SKU + ຈຳນວນຊຸດ" },
  { title: "ກວດສ່ວນປະກອບ", detail: "ສະຕັອກຊິ້ນຍ່ອຍ" },
  { title: "ຢືນຢັນ", detail: "+ສະຕັອກຊຸດ + lot" },
] as const;

const DISASSEMBLE_FLOW_STEPS = [
  { title: "ເລືອກເຊັດ", detail: "SKU + ຈຳນວນຊຸດ" },
  { title: "ກວດສະຕັອກຊຸດ", detail: "ສະຕັອກ ແລະ lot" },
  { title: "ຢືນຢັນ", detail: "−ຊຸດ → ຄືນຊິ້ນຍ່ອຍ" },
] as const;

function FlowStrip({ steps }: { steps: readonly { title: string; detail: string }[] }) {
  return (
    <ol className="grid gap-2 sm:grid-cols-3">
      {steps.map((step, index) => (
        <li
          key={step.title}
          className="flex items-center gap-3 rounded-xl border border-secondary/20 bg-card/60 px-3 py-2.5"
        >
          <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/20 font-mono text-secondary text-xs tabular-nums">
            {index + 1}
          </span>
          <div className="min-w-0">
            <p className="font-medium text-foreground text-sm leading-tight">
              {step.title}
            </p>
            <p className="text-muted-foreground text-xs">{step.detail}</p>
          </div>
          {index < steps.length - 1 ? (
            <ArrowRight className="ml-auto hidden size-4 shrink-0 text-muted-foreground/60 sm:block" />
          ) : null}
        </li>
      ))}
    </ol>
  );
}

function StatTile({
  label,
  value,
  hint,
  tone = "default",
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: "default" | "success" | "warning" | "danger";
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-1 rounded-xl border px-3 py-2.5",
        tone === "success" && "border-leaf/30 bg-leaf/5",
        tone === "warning" && "border-amber-600/25 bg-amber-500/5",
        tone === "danger" && "border-destructive/25 bg-destructive/5",
        tone === "default" && "border-secondary/15 bg-background/50",
      )}
    >
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="font-semibold text-foreground text-sm tabular-nums">
        {value}
      </span>
      {hint ? (
        <span className="text-muted-foreground text-xs">{hint}</span>
      ) : null}
    </div>
  );
}

function ComponentLineRow({
  line,
}: {
  line: BundleAssemblyPreviewDTO["lines"][number];
}) {
  const coverage =
    line.quantityNeeded > 0
      ? Math.min(100, (line.availableStock / line.quantityNeeded) * 100)
      : 100;

  return (
    <li className="flex flex-col gap-2 rounded-xl border border-secondary/15 bg-background/50 px-3 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-foreground text-sm leading-snug">
            {line.componentName}
          </p>
          <p className="mt-0.5 text-muted-foreground text-xs tabular-nums">
            {line.quantityPerBundle} / ຊຸດ · ຕ້ອງໃຊ້ {line.quantityNeeded}
          </p>
        </div>
        {line.sufficient ? (
          <CircleCheckIcon className="size-4 shrink-0 text-leaf" />
        ) : (
          <TriangleAlertIcon className="size-4 shrink-0 text-destructive" />
        )}
      </div>
      <div className="flex items-center gap-2">
        <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-secondary/10">
          <div
            className={cn(
              "h-full rounded-full transition-[width]",
              line.sufficient ? "bg-leaf" : "bg-destructive",
            )}
            style={{ width: `${coverage}%` }}
          />
        </div>
        <span
          className={cn(
            "shrink-0 text-xs tabular-nums",
            line.sufficient ? "text-muted-foreground" : "text-destructive",
          )}
        >
          ມີ {line.availableStock}
        </span>
      </div>
    </li>
  );
}

function DisassemblyComponentRow({
  line,
}: {
  line: BundleDisassemblyPreviewDTO["lines"][number];
}) {
  return (
    <li className="flex flex-col gap-1 rounded-xl border border-secondary/15 bg-background/50 px-3 py-3">
      <p className="font-medium text-foreground text-sm leading-snug">
        {line.componentName}
      </p>
      <p className="text-muted-foreground text-xs tabular-nums">
        {line.quantityPerBundle} / ຊຸດ · ຈະຄືນ {line.quantityReturned}
      </p>
    </li>
  );
}

function DisassemblyPreviewPanel({
  selectedLabel,
  quantity,
  preview,
  isLoading,
  isError,
  error,
}: {
  selectedLabel: string;
  quantity: number;
  preview: ReturnType<typeof useBundleDisassemblyPreview>;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}) {
  const data = preview.data;

  return (
    <ProductFormSection
      icon={Package}
      title="ຕົວຢ່າງກ່ອນແຍກຊຸດ"
      description={selectedLabel}
      className="h-full lg:sticky lg:top-4"
    >
      {isLoading ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-16 w-full rounded-xl" />
          <Skeleton className="h-20 w-full rounded-xl" />
        </div>
      ) : isError ? (
        <div className="flex items-start gap-2 rounded-xl border border-destructive/25 bg-destructive/5 px-3 py-3 text-sm">
          <TriangleAlertIcon className="mt-0.5 size-4 shrink-0 text-destructive" />
          <p className="text-destructive">
            {error instanceof Error ? error.message : "ໂຫຼດຕົວຢ່າງບໍ່ໄດ້"}
          </p>
        </div>
      ) : data ? (
        <div className="flex flex-col gap-4">
          <div className="grid gap-2 sm:grid-cols-3">
            <StatTile
              label="ຈຳນວນທີ່ຈະແຍກ"
              value={`${quantity} ຊຸດ`}
            />
            <StatTile
              label="ແຍກໄດ້ສູງສຸດ"
              value={`${data.maxDisassemblableQty} ຊຸດ`}
              tone={
                quantity > data.maxDisassemblableQty ? "danger" : "success"
              }
            />
            <StatTile
              label="ສະຕັອກຊຸດປັດຈຸບັນ"
              value={`${data.bundleStockAvailable} ຊຸດ`}
              hint={`lot ຄົບ ${data.lotBackedQty}`}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {data.canDisassemble ? (
              <Badge className="rounded-full border-leaf/30 bg-leaf/10 text-leaf">
                <CircleCheckIcon data-icon="inline-start" className="size-3" />
                ພ້ອມແຍກຊຸດ
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="rounded-full border-destructive/30 bg-destructive/5 text-destructive"
              >
                <TriangleAlertIcon data-icon="inline-start" className="size-3" />
                ສະຕັອກຊຸດບໍ່ພໍ
              </Badge>
            )}
            <span className="text-muted-foreground text-xs">
              ຫັກສະຕັອກຊຸດ + lot → ຄືນຊິ້ນຍ່ອຍແຕ່ລະລາຍການ
            </span>
          </div>

          <ul className="flex flex-col gap-2">
            {data.lines.map((line) => (
              <DisassemblyComponentRow key={line.componentVariantId} line={line} />
            ))}
          </ul>

          {!data.canDisassemble ? (
            <p className="rounded-xl border border-destructive/20 bg-destructive/5 px-3 py-2.5 text-destructive text-sm">
              ຫຼຸດຈຳນວນຊຸດ ຫຼື ປະກອບ/ຮັບເຂົ້າຊຸດກ່ອນ (ຕ້ອງມີ lot ຄົບ)
            </p>
          ) : null}
        </div>
      ) : (
        <p className="text-muted-foreground text-sm">
          ເລືອກເຊັດ ແລະ ຈຳນວນຊຸດເພື່ອເບິ່ງຕົວຢ່າງ
        </p>
      )}
    </ProductFormSection>
  );
}

function AssemblyPreviewPanel({
  selectedLabel,
  quantity,
  preview,
  isLoading,
  isError,
  error,
}: {
  selectedLabel: string;
  quantity: number;
  preview: ReturnType<typeof useBundleAssemblyPreview>;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}) {
  const data = preview.data;

  return (
    <ProductFormSection
      icon={Package}
      title="ຕົວຢ່າງກ່ອນປະກອບ"
      description={selectedLabel}
      className="h-full lg:sticky lg:top-4"
    >
      {isLoading ? (
        <div className="flex flex-col gap-3">
          <div className="grid gap-2 sm:grid-cols-3">
            <Skeleton className="h-16 rounded-xl" />
            <Skeleton className="h-16 rounded-xl" />
            <Skeleton className="h-16 rounded-xl" />
          </div>
          <Skeleton className="h-20 w-full rounded-xl" />
          <Skeleton className="h-20 w-full rounded-xl" />
        </div>
      ) : isError ? (
        <div className="flex items-start gap-2 rounded-xl border border-destructive/25 bg-destructive/5 px-3 py-3 text-sm">
          <TriangleAlertIcon className="mt-0.5 size-4 shrink-0 text-destructive" />
          <p className="text-destructive">
            {error instanceof Error ? error.message : "ໂຫຼດຕົວຢ່າງບໍ່ໄດ້"}
          </p>
        </div>
      ) : data ? (
        <div className="flex flex-col gap-4">
          <div className="grid gap-2 sm:grid-cols-3">
            <StatTile
              label="ຈຳນວນທີ່ຈະປະກອບ"
              value={`${quantity} ຊຸດ`}
            />
            <StatTile
              label="ປະກອບໄດ້ສູງສຸດ"
              value={`${data.maxAssemblableQty} ຊຸດ`}
              tone={
                quantity > data.maxAssemblableQty ? "danger" : "success"
              }
            />
            <StatTile
              label="ຕົ້ນທຸນໂດຍປະມານ"
              value={formatLak(data.estimatedUnitLandedCost)}
              hint="/ ຊຸດ"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {data.canAssemble ? (
              <Badge className="rounded-full border-leaf/30 bg-leaf/10 text-leaf">
                <CircleCheckIcon data-icon="inline-start" className="size-3" />
                ພ້ອມປະກອບ
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="rounded-full border-destructive/30 bg-destructive/5 text-destructive"
              >
                <TriangleAlertIcon data-icon="inline-start" className="size-3" />
                ສ່ວນປະກອບບໍ່ພໍ
              </Badge>
            )}
            <span className="text-muted-foreground text-xs">
              ຫັກຈາກສະຕັອກຊິ້ນຍ່ອຍ → ເພີ່ມສະຕັອກຊຸດແຍກຕ່າງຫາກ
            </span>
          </div>

          <ul className="flex flex-col gap-2">
            {data.lines.map((line) => (
              <ComponentLineRow
                key={line.componentVariantId}
                line={line}
              />
            ))}
          </ul>

          {!data.canAssemble ? (
            <p className="rounded-xl border border-destructive/20 bg-destructive/5 px-3 py-2.5 text-destructive text-sm">
              ຫຼຸດຈຳນວນຊຸດ ຫຼື ເພີ່ມສະຕັອກຊິ້ນຍ່ອຍກ່ອນຢືນຢັນ
            </p>
          ) : null}
        </div>
      ) : (
        <p className="text-muted-foreground text-sm">
          ເລືອກເຊັດ ແລະ ຈຳນວນຊຸດເພື່ອເບິ່ງຕົວຢ່າງ
        </p>
      )}
    </ProductFormSection>
  );
}

function BundleAssemblyForm({
  bundleOptions,
  canWrite,
}: {
  bundleOptions: { value: string; label: string }[];
  canWrite: boolean;
}) {
  const assemble = useAssembleBundle();

  const methods = RHF.useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      bundleVariantId: bundleOptions[0]?.value ?? "",
      quantity: 1,
      note: "",
    },
  });

  const bundleVariantId = methods.watch("bundleVariantId");
  const quantity = methods.watch("quantity");

  useEffect(() => {
    if (bundleOptions.length === 0) return;
    const valid = bundleOptions.some((opt) => opt.value === bundleVariantId);
    if (!valid) {
      const first = bundleOptions[0];
      if (first) {
        methods.setValue("bundleVariantId", first.value, {
          shouldValidate: true,
        });
      }
    }
  }, [bundleOptions, bundleVariantId, methods]);

  const preview = useBundleAssemblyPreview(
    bundleVariantId,
    quantity > 0 ? quantity : undefined,
  );

  const selectedLabel =
    bundleOptions.find((opt) => opt.value === bundleVariantId)?.label ?? "—";

  const maxQty = preview.data?.maxAssemblableQty ?? 0;

  function applyMaxQuantity() {
    if (maxQty < 1) return;
    methods.setValue("quantity", maxQty, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }

  async function onSubmit(values: FormValues) {
    try {
      const result = await assemble.mutateAsync(values);
      toast.success(
        `ປະກອບ ${result.quantity} ຊຸດສຳເລັດ — ສະຕັອກເຫຼືອ ${result.bundleStockQuantity}`,
      );
      methods.reset({ ...values, note: "" });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ປະກອບລົ້ມເຫຼວ");
    }
  }

  return (
    <FormRoot
      methods={methods}
      onSubmit={onSubmit}
      onInvalid={() => {
        toast.error("ກະລຸນາກວດສອບຂໍ້ມູນໃນຟອມ");
      }}
      className="gap-6"
    >
      <FlowStrip steps={ASSEMBLE_FLOW_STEPS} />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,22rem)_1fr] lg:items-start">
        <ProductFormSection
          icon={Package}
          title="ຂໍ້ມູນການປະກອບ"
          description="ເລືອກ SKU ເຊັດ ແລະ ຈຳນວນຊຸດ"
        >
          <RHF.Controller
            name="bundleVariantId"
            control={methods.control}
            render={({ field, fieldState }) => (
              <FieldLayout data-invalid={fieldState.invalid || undefined}>
                <FieldLabel>SKU ເຊັດ</FieldLabel>
                <FieldContent>
                  <SimpleSelect
                    value={
                      bundleOptions.some((opt) => opt.value === field.value)
                        ? field.value
                        : undefined
                    }
                    onValueChange={field.onChange}
                    options={bundleOptions}
                    placeholder="ເລືອກເຊັດ"
                    className="w-full"
                  />
                  {fieldState.error ? (
                    <FieldError>{fieldState.error.message}</FieldError>
                  ) : null}
                </FieldContent>
              </FieldLayout>
            )}
          />

          <div className="flex flex-col gap-2">
            <FormInput
              name="quantity"
              label="ຈຳນວນຊຸດທີ່ຈະປະກອບ"
              type="number"
              min={1}
            />
            {maxQty > 0 ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-fit rounded-full border-secondary/40"
                onClick={applyMaxQuantity}
              >
                ໃຊ້ຈຳນວນສູງສຸດ ({maxQty})
              </Button>
            ) : null}
          </div>

          <FormInput name="note" label="ໝາຍເຫດ (ທາງເລືອກ)" />
        </ProductFormSection>

        <AssemblyPreviewPanel
          selectedLabel={selectedLabel}
          quantity={quantity}
          preview={preview}
          isLoading={preview.isLoading}
          isError={preview.isError}
          error={preview.error}
        />
      </div>

      {canWrite ? (
        <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-secondary/25 bg-card/95 p-4 shadow-lg shadow-secondary/10 backdrop-blur-sm sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none sm:backdrop-blur-none">
          <p className="text-muted-foreground text-sm">
            {preview.data?.canAssemble
              ? "ກວດສອບຕົວຢ່າງແລ້ວກົດຢືນຢັນ"
              : "ປັບຈຳນວນ ຫຼື ເພີ່ມສະຕັອກຊິ້ນຍ່ອຍກ່ອນ"}
          </p>
          <FormActions className="pt-0">
            <Button
              type="submit"
              className="rounded-full px-6"
              disabled={
                assemble.isPending ||
                !preview.data?.canAssemble ||
                !bundleVariantId
              }
              isLoading={assemble.isPending}
            >
              ຢືນຢັນປະກອບຊຸດ
            </Button>
          </FormActions>
        </div>
      ) : (
        <p className="text-muted-foreground text-sm">
          ບໍ່ມີສິດ inventory:write — ເບິ່ງຕົວຢ່າງໄດ້ຢ່າງດຽວ
        </p>
      )}
    </FormRoot>
  );
}

function BundleDisassemblyForm({
  bundleOptions,
  canWrite,
}: {
  bundleOptions: { value: string; label: string }[];
  canWrite: boolean;
}) {
  const disassemble = useDisassembleBundle();

  const methods = RHF.useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      bundleVariantId: bundleOptions[0]?.value ?? "",
      quantity: 1,
      note: "",
    },
  });

  const bundleVariantId = methods.watch("bundleVariantId");
  const quantity = methods.watch("quantity");

  useEffect(() => {
    if (bundleOptions.length === 0) return;
    const valid = bundleOptions.some((opt) => opt.value === bundleVariantId);
    if (!valid) {
      const first = bundleOptions[0];
      if (first) {
        methods.setValue("bundleVariantId", first.value, {
          shouldValidate: true,
        });
      }
    }
  }, [bundleOptions, bundleVariantId, methods]);

  const preview = useBundleDisassemblyPreview(
    bundleVariantId,
    quantity > 0 ? quantity : undefined,
  );

  const selectedLabel =
    bundleOptions.find((opt) => opt.value === bundleVariantId)?.label ?? "—";

  const maxQty = preview.data?.maxDisassemblableQty ?? 0;

  function applyMaxQuantity() {
    if (maxQty < 1) return;
    methods.setValue("quantity", maxQty, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }

  async function onSubmit(values: FormValues) {
    try {
      const result = await disassemble.mutateAsync(values);
      toast.success(
        `ແຍກຊຸດ ${result.quantity} ສຳເລັດ — ສະຕັອກຊຸດເຫຼືອ ${result.bundleStockQuantity}`,
      );
      methods.reset({ ...values, note: "" });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ແຍກຊຸດລົ້ມເຫຼວ");
    }
  }

  return (
    <FormRoot
      methods={methods}
      onSubmit={onSubmit}
      onInvalid={() => {
        toast.error("ກະລຸນາກວດສອບຂໍ້ມູນໃນຟອມ");
      }}
      className="gap-6"
    >
      <FlowStrip steps={DISASSEMBLE_FLOW_STEPS} />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,22rem)_1fr] lg:items-start">
        <ProductFormSection
          icon={Package}
          title="ຂໍ້ມູນການແຍກຊຸດ"
          description="ເລືອກ SKU ເຊັດທີ່ປະກອບ/ຮັບເຂົ້າແລ້ວ"
        >
          <RHF.Controller
            name="bundleVariantId"
            control={methods.control}
            render={({ field, fieldState }) => (
              <FieldLayout data-invalid={fieldState.invalid || undefined}>
                <FieldLabel>SKU ເຊັດ</FieldLabel>
                <FieldContent>
                  <SimpleSelect
                    value={
                      bundleOptions.some((opt) => opt.value === field.value)
                        ? field.value
                        : undefined
                    }
                    onValueChange={field.onChange}
                    options={bundleOptions}
                    placeholder="ເລືອກເຊັດ"
                    className="w-full"
                  />
                  {fieldState.error ? (
                    <FieldError>{fieldState.error.message}</FieldError>
                  ) : null}
                </FieldContent>
              </FieldLayout>
            )}
          />

          <div className="flex flex-col gap-2">
            <FormInput
              name="quantity"
              label="ຈຳນວນຊຸດທີ່ຈະແຍກ"
              type="number"
              min={1}
            />
            {maxQty > 0 ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-fit rounded-full border-secondary/40"
                onClick={applyMaxQuantity}
              >
                ໃຊ້ຈຳນວນສູງສຸດ ({maxQty})
              </Button>
            ) : null}
          </div>

          <FormInput name="note" label="ໝາຍເຫດ (ທາງເລືອກ)" />
        </ProductFormSection>

        <DisassemblyPreviewPanel
          selectedLabel={selectedLabel}
          quantity={quantity}
          preview={preview}
          isLoading={preview.isLoading}
          isError={preview.isError}
          error={preview.error}
        />
      </div>

      {canWrite ? (
        <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-secondary/25 bg-card/95 p-4 shadow-lg shadow-secondary/10 backdrop-blur-sm sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none sm:backdrop-blur-none">
          <p className="text-muted-foreground text-sm">
            {preview.data?.canDisassemble
              ? "ກວດສອບຕົວຢ່າງແລ້ວກົດຢືນຢັນ"
              : "ປັບຈຳນວນ ຫຼື ເພີ່ມສະຕັອກຊຸດກ່ອນ"}
          </p>
          <FormActions className="pt-0">
            <Button
              type="submit"
              className="rounded-full px-6"
              disabled={
                disassemble.isPending ||
                !preview.data?.canDisassemble ||
                !bundleVariantId
              }
              isLoading={disassemble.isPending}
            >
              ຢືນຢັນແຍກຊຸດ
            </Button>
          </FormActions>
        </div>
      ) : (
        <p className="text-muted-foreground text-sm">
          ບໍ່ມີສິດ inventory:write — ເບິ່ງຕົວຢ່າງໄດ້ຢ່າງດຽວ
        </p>
      )}
    </FormRoot>
  );
}

export function BundleAssemblyPage() {
  const nav = useNavigate();
  const canWrite = useActionPermission("inventory:write");
  const picker = useProductPickerQuery({ purpose: "order" });

  const bundleOptions = useMemo(() => {
    const items = picker.data?.data ?? [];
    const options: { value: string; label: string }[] = [];
    for (const product of items) {
      for (const variant of product.variants) {
        if (variant.variantType !== "bundle") continue;
        if (!variant.bundleLineCount || variant.bundleLineCount < 1) continue;
        options.push({
          value: variant.id,
          label: `${product.name} · ${variant.name}`,
        });
      }
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [picker.data?.data]);

  return (
    <>
      <Header />
      <Main>
        <div className="mx-auto w-full max-w-5xl">
          <ProductFormPageHeader
            title="ປະກອບ / ແຍກຊຸດ"
            subtitle="ປະກອບ: ຫັກຊິ້ນຍ່ອຍ → ຊຸດ · ແຍກຊຸດ: ຄືນຊິ້ນຍ່ອຍຈາກຊຸດທີ່ປະກອບແລ້ວ"
            onBack={() => nav({ to: "/app/inventory" })}
          />

          {picker.isLoading ? (
            <div className="flex flex-col gap-4">
              <Skeleton className="h-14 w-full rounded-xl" />
              <div className="grid gap-4 lg:grid-cols-[22rem_1fr]">
                <Skeleton className="h-72 rounded-2xl" />
                <Skeleton className="h-96 rounded-2xl" />
              </div>
            </div>
          ) : bundleOptions.length === 0 ? (
            <div className="brand-card p-8">
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Package />
                  </EmptyMedia>
                  <EmptyTitle>ຍັງບໍ່ມີເຊັດທີ່ພ້ອມປະກອບ</EmptyTitle>
                  <EmptyDescription>
                    ສ້າງ SKU ເຊັດໃນສິນຄ້າ ແລະ ເພີ່ມສ່ວນປະກອບ (BOM) ກ່ອນ
                  </EmptyDescription>
                </EmptyHeader>
                <Button asChild className="rounded-full">
                  <Link to="/app/products/new">ສ້າງເຊັດໃນສິນຄ້າ</Link>
                </Button>
              </Empty>
            </div>
          ) : (
            <Tabs defaultValue="assemble" className="gap-6">
              <TabsList className="w-full max-w-md">
                <TabsTrigger value="assemble" className="flex-1">
                  ປະກອບຊຸດ
                </TabsTrigger>
                <TabsTrigger value="disassemble" className="flex-1">
                  ແຍກຊຸດ
                </TabsTrigger>
              </TabsList>
              <TabsContent value="assemble">
                <BundleAssemblyForm
                  key={`asm-${bundleOptions.map((opt) => opt.value).join(",")}`}
                  bundleOptions={bundleOptions}
                  canWrite={canWrite}
                />
              </TabsContent>
              <TabsContent value="disassemble">
                <BundleDisassemblyForm
                  key={`dis-${bundleOptions.map((opt) => opt.value).join(",")}`}
                  bundleOptions={bundleOptions}
                  canWrite={canWrite}
                />
              </TabsContent>
            </Tabs>
          )}
        </div>
      </Main>
    </>
  );
}
